/**
 * GA4 + Google Ads gtag.js loader.
 *
 * Loads the gtag.js script immediately on init so page views and events are
 * captured from the first render.  Consent can still be updated at runtime
 * via `window.vidSeedsConsent` (e.g. from a future cookie banner).
 *
 * GA4 Measurement ID: G-DTQ6JYMKV3 (override via NEXT_PUBLIC_GA_MEASUREMENT_ID)
 * Google Ads ID: AW-17743054181 (override via NEXT_PUBLIC_GOOGLE_ADS_ID)
 */

import { createErrorReporter } from "@/lib/observability/report-error";

const report = createErrorReporter("lib.analytics.gtagLoader", "analytics");

type GaConsentUpdate = Partial<{
  ad_storage: "granted" | "denied";
  ad_user_data: "granted" | "denied";
  ad_personalization: "granted" | "denied";
  analytics_storage: "granted" | "denied";
}>;

const DEFAULT_GA4_MEASUREMENT_ID = "G-DTQ6JYMKV3";
const GTAG_SRC_PREFIX = "https://www.googletagmanager.com/gtag/js?id=";

function getMeasurementId(): string {
  const fromEnv = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return DEFAULT_GA4_MEASUREMENT_ID;
}

function getGoogleAdsId(): string | null {
  const fromEnv = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return null;
}

function injectGtagScript(measurementId: string): void {
  if (typeof document === "undefined") return;

  const existing = document.querySelector(`script[src^="${GTAG_SRC_PREFIX}"]`);
  if (existing) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTAG_SRC_PREFIX}${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

function ensureDataLayer(): void {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    const gtagFn: Window["gtag"] = ((
      command: string,
      eventNameOrConfig: string,
      params?: Record<string, unknown>,
    ) => {
      window.dataLayer?.push([command, eventNameOrConfig, params]);
    }) as never;
    window.gtag = gtagFn;
  }
}

export function initGtagLoader(): void {
  if (typeof window === "undefined") return;

  const measurementId = getMeasurementId();
  const googleAdsId = getGoogleAdsId();

  // Load gtag.js immediately — basic analytics is legitimate interest.
  ensureDataLayer();
  injectGtagScript(measurementId);

  // Initialize GA4 config
  window.gtag?.("js", new Date().toISOString());
  window.gtag?.("config", measurementId);

  // Initialize Google Ads conversion tracking (if configured)
  if (googleAdsId) {
    window.gtag?.("config", googleAdsId);
  }

  // Expose consent API for future cookie banner / preference center.
  window.vidSeedsConsent = {
    update: (options?: GaConsentUpdate) => {
      try {
        window.gtag?.("consent", "update", options || {});
      } catch (error) {
        console.error("[analytics] consent update failed", error);
        report(error, { action: "consentUpdate", severity: "warning" });
      }
    },
    grantAll: () => {
      try {
        window.gtag?.("consent", "update", {
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "granted",
          analytics_storage: "granted",
        });
      } catch (error) {
        console.error("[analytics] consent grantAll failed", error);
        report(error, { action: "consentGrantAll", severity: "warning" });
      }
    },
    denyAll: () => {
      try {
        window.gtag?.("consent", "update", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: "denied",
        });
      } catch (error) {
        console.error("[analytics] consent denyAll failed", error);
        report(error, { action: "consentDenyAll", severity: "warning" });
      }
    },
  };
}

/**
 * Set GA4 user_id for the current session. Call this after authentication
 * to associate the authenticated user with all subsequent events and to
 * enable GA4's "new user" detection to work correctly across sessions.
 *
 * Must be called after initGtagLoader() and once the user id is known.
 */
export function setGtagUserId(userId: string): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.gtag?.("set", { user_id: userId });
  } catch (error) {
    console.error("[analytics] setGtagUserId failed", error);
    report(error, { action: "setUserId", severity: "warning" });
  }
}
